import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { FileText, Save } from 'lucide-react';

const Notes = ({ currentDate }) => {
  const [notes, setNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const dateString = format(currentDate, 'yyyy-MM-dd');

  // Load notes for the current date
  useEffect(() => {
    const fetchNotes = async () => {
      setIsLoading(true);
      try {
        const response = await fetch(`/api/notes/${dateString}`);
        const data = await response.json();
        setNotes(data.notes || '');
        setLastSaved(data.updated_at);
      } catch (error) {
        console.error('Error fetching notes:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNotes();
  }, [dateString]);

  // Save notes with debouncing
  useEffect(() => {
    if (notes === '' && !lastSaved) return; // Don't save empty notes on initial load

    const timeoutId = setTimeout(() => {
      saveNotes();
    }, 1000); // Save 1 second after user stops typing

    return () => clearTimeout(timeoutId);
  }, [notes, dateString, lastSaved]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveNotes = async () => {
    setIsSaving(true);
    try {
      const response = await fetch('/api/notes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: dateString,
          notes: notes
        }),
      });

      if (response.ok) {
        setLastSaved(new Date().toISOString());
      }
    } catch (error) {
      console.error('Error saving notes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNotesChange = (e) => {
    setNotes(e.target.value);
  };

  return (
    <div className="notes-section">
      <div className="notes-header">
        <div className="notes-title">
          <FileText size={20} />
          <h3>Daily Notes</h3>
        </div>
        <div className="notes-status">
          {isSaving && (
            <div className="saving-indicator">
              <Save size={16} />
              <span>Saving...</span>
            </div>
          )}
          {lastSaved && !isSaving && (
            <div className="saved-indicator">
              <span>Saved {format(new Date(lastSaved), 'HH:mm')}</span>
            </div>
          )}
        </div>
      </div>
      
      <div className="notes-content">
        {isLoading ? (
          <div className="notes-loading">
            <div className="loading-spinner"></div>
            <span>Loading notes...</span>
          </div>
        ) : (
          <textarea
            className="notes-textarea"
            value={notes}
            onChange={handleNotesChange}
            placeholder="Add your daily notes, reminders, or observations here..."
            rows={8}
          />
        )}
      </div>
    </div>
  );
};

export default Notes;
